import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../api/authAPI';
import { getToken, removeToken } from '../utils/auth';
import { initFCM, unregisterFCMToken } from '../utils/fcm';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string, name: string, nickname?: string, phone?: string, birthDate?: string, gender?: string) => Promise<any>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 페이지 로드시 토큰 검증 및 사용자 정보 로드
  useEffect(() => {
    const initAuth = async () => {
      console.log('AuthContext: 초기화 시작');
      const storedToken = getToken();
      
      if (!storedToken) {
        console.log('AuthContext: 토큰 없음');
        setLoading(false);
        return;
      }

      setToken(storedToken);

      try {
        console.log('AuthContext: 토큰 검증 시도');
        const response = await authAPI.verifyToken();
        setUser(response.user);
        console.log('AuthContext: 사용자 정보 로드 완료', response.user);
        // 로그인 상태 복원 시 FCM 초기화 (Firebase 설정이 없으면 내부에서 자동 스킵)
        initFCM().catch(() => {});
      } catch (error) {
        console.error('AuthContext: 토큰 검증 실패', error);
        removeToken();
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    console.log('AuthContext: 로그인 시도');
    const response = await authAPI.login(email, password);
    setUser(response.user);
    setToken(response.token || getToken());
    console.log('AuthContext: 로그인 완료', response.user);
    // 로그인 성공 후 FCM 초기화
    initFCM().catch(() => {});
    return response;
  };

  const register = async (email: string, password: string, name: string, nickname?: string, phone?: string, birthDate?: string, gender?: string) => {
    console.log('AuthContext: 회원가입 시도');
    const response = await authAPI.register(email, password, name, nickname, phone, birthDate, gender);
    console.log('AuthContext: 회원가입 완료');
    return response;
  };

  const logout = () => {
    console.log('AuthContext: 로그아웃');
    // FCM 토큰 서버에서 삭제 (캐싱된 토큰 사용)
    const cachedToken = localStorage.getItem('fcm_token') || undefined;
    unregisterFCMToken(cachedToken).catch(() => {});
    localStorage.removeItem('fcm_token');
    authAPI.logout();
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    console.log('AuthContext: 사용자 정보 새로고침');
    try {
      const response = await authAPI.getMe();
      setUser(response.user);
      console.log('AuthContext: 사용자 정보 새로고침 완료', response.user);
    } catch (error) {
      console.error('AuthContext: 사용자 정보 새로고침 실패', error);
      removeToken();
      setUser(null);
      setToken(null);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
